import {Icon} from '@benchling/nucleus-ui';
import {Alert} from '@mantine/core';
import React from 'react';

import useMoleculeSchemasPairing from 'chemistry/bulk-upload/useMoleculeSchemasPairing';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';

/**
 * Whether the molecules being created have salts or solvents. Salted molecules are created on the Form
 * schema and may auto-create a parent on the Compound schema; unsalted molecules are created on the
 * Compound schema.
 */
export type MoleculeStructureKind = 'salted' | 'unsalted' | 'mixed';

/**
 * Returns the naming strategy an auto-created parent Compound is registered with.
 * Auto-created Compounds have synthetic names, so name-derived registry IDs are replaced with new IDs.
 */
function getParentNamingStrategy(namingStrategy: RegistryLabelingStrategy): RegistryLabelingStrategy {
  return namingStrategy === RegistryLabelingStrategy.IDS_FROM_NAMES
    ? RegistryLabelingStrategy.NEW_IDS
    : namingStrategy;
}

interface LinkedSchemaRegistration {
  namingStrategy: RegistryLabelingStrategy;
  /** Describes the molecules that are registered on the linked schema with this naming strategy. */
  affectedMolecules: string;
}

/**
 * Lists the ways molecules can be registered on the schema linked to the selected one, given the kind of
 * structures being created.
 */
function getLinkedSchemaRegistrations(
  isFormSelected: boolean,
  namingStrategy: RegistryLabelingStrategy,
  structureKind: MoleculeStructureKind
): Array<LinkedSchemaRegistration> {
  const hasSalted = structureKind !== 'unsalted';
  const hasUnsalted = structureKind !== 'salted';
  const registrations: Array<LinkedSchemaRegistration> = [];

  if (isFormSelected) {
    if (hasSalted) {
      registrations.push({
        namingStrategy: getParentNamingStrategy(namingStrategy),
        affectedMolecules: 'any salted or solvent molecule whose Compound must be created',
      });
    }
    if (hasUnsalted) {
      registrations.push({
        namingStrategy,
        affectedMolecules: 'any molecule without salts or solvents, which is created as a Compound',
      });
    }
  } else if (hasSalted) {
    registrations.push({
      namingStrategy,
      affectedMolecules: 'any salted or solvent molecule, which is created as a Form',
    });
  }
  return registrations;
}

interface LinkedSchemaNamingStrategyWarningProps {
  /** The schema the user selected, or null when none is selected. */
  selectedSchemaId: string | null;
  namingStrategy: RegistryLabelingStrategy | null;
  structureKind: MoleculeStructureKind;
}

/**
 * Warning shown when molecules are registered with a naming strategy that the linked Form or Compound
 * schema does not enable. Creating a molecule can also create a molecule on the linked schema, and that
 * creation fails if the strategy is not enabled there.
 */
export default function LinkedSchemaNamingStrategyWarning({
  selectedSchemaId,
  namingStrategy,
  structureKind,
}: LinkedSchemaNamingStrategyWarningProps): JSX.Element | null {
  const {pairingMap, schemasById} = useMoleculeSchemasPairing(null);
  const pairing = selectedSchemaId != null ? pairingMap[selectedSchemaId] : undefined;

  if (pairing == null || namingStrategy == null) {
    return null;
  }

  const isFormSelected = pairing.formSchemaId === selectedSchemaId;
  const linkedSchema = schemasById[isFormSelected ? pairing.compoundSchemaId : pairing.formSchemaId];
  if (linkedSchema == null) {
    return null;
  }

  const failures = getLinkedSchemaRegistrations(isFormSelected, namingStrategy, structureKind).filter(
    (registration) => !linkedSchema.labelingStrategies.includes(registration.namingStrategy)
  );
  if (failures.length === 0) {
    return null;
  }

  return (
    <Alert
      variant="warning"
      mb="md"
      icon={<Icon icon="exclamation-circle" />}
      styles={{root: {flexShrink: 0}}}
      title={`The linked schema ${linkedSchema.name} does not enable this naming strategy.`}
    >
      {failures.map(({namingStrategy: failedStrategy, affectedMolecules}) => (
        <div key={failedStrategy + affectedMolecules}>
          Registration will fail for {affectedMolecules}, because it is registered with the naming strategy{' '}
          {failedStrategy}.
        </div>
      ))}
      <div>Select a different naming strategy, or enable it on {linkedSchema.name}.</div>
    </Alert>
  );
}
